/**
 * inventoryService.ts — Servicio CANÓNICO del agregado Inventory (frontend)
 *
 * PR-31 — Consolidación Frontend Servicios Inventario
 * Ownership: agregado Inventory (catálogo CRUD, balance, QC, alertas, movimientos,
 *           requests 1:1, transfers, stats, reports, attachments)
 *
 * Backend canónico alineado:
 *   • inventoryController.enterprise.js (PR-29)
 *   • InventoryBalanceService            (PR-29)
 *   • InventoryMovementService            (PR-29)
 *
 * Decisiones de dominio aplicadas:
 *   #1 Reserva ≠ movimiento ledger (sin llamadas a movimiento al reservar)
 *   #2 Request mantiene diseño 1:1
 *   #3 Dos tablas + servicio unificado backend (este servicio NO mezcla workspace)
 *   #5 QC = transiciones de balance
 *   #6 Transfer: RETIRADO en P13-V2B (superseded-map fila 6) — el traslado comercial viaja por
 *      outboundService (una sola autoridad); el PUT singular y su clave aleatoria por llamada
 *      eran la única mutación fuera del ciclo M-6
 *
 * Servicios que NO compiten con este:
 *   • outboundService.ts        → OutboundOrder (subdominio separado)
 *   • inventoryWorkspaceService.ts → Workspace operations
 *   • inventoryVisibilityService.ts → Visibility/scope (solo lectura)
 */
// ==========================================
// 🛠️ SERVICIO DE INVENTARIO MGI
// ==========================================

import { apiClient } from './api-client'
import { fetchAllPointPages, sharedClientPointPages } from './pointCatalogue'
import { extractInventoryError } from './errorMaps/inventory'
import { measureApi } from './utils/measureApi'
import { withNormalized } from './utils/normalizeInventoryRequest'
import type { 
  InventoryItem, 
  StockAlert, 
  StockRequest,
  InventoryMovement,
  InventoryStats,
  Agency,
  InventoryFilters,
  InventoryResponse,
  InventoryReport,
  InventoryAudit
} from '../types/inventory'
import { normalizeCentralInventoryItem } from './utils/normalizeCentralInventoryItem'

// Nota: Se eliminan fallbacks/mock; sólo datos reales del backend

// ─────────────────────────────────────────────────────────────────────────────
// P13-V2A C7 — ciclo de vida CONGELADO de la Idempotency-Key (backend M-6):
//   · la clave se crea UNA vez por INTENTO comercial y se REUTILIZA en cada
//     reintento de ese intento (jamás se regenera por llamada HTTP — el
//     generador aleatorio por llamada era inútil para retry);
//   · se descarta sólo ante una respuesta TERMINAL (éxito o error no
//     reintientable); un fallo de red / 5xx / 409 RETRYABLE conserva la clave
//     para que el reintento sea un REPLAY estable del backend;
//   · una operación de usuario conscientemente NUEVA genera clave nueva
//     (el scope del intento queda liberado al liquidarse el anterior).
// Compartido por outboundService/inventoryWorkspaceService (una sola autoridad).
// ─────────────────────────────────────────────────────────────────────────────
const idempotencyAttemptKeys = new Map<string, string>()

function randomKeySuffix(): string {
  const c: any = (globalThis as any).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** Hash estable (djb2) para derivar el scope de intento de un payload de creación.
 *  TR4: serialización CANÓNICA (claves ordenadas recursivamente) — un payload materialmente
 *  idéntico jamás produce churn de clave sólo por el orden de inserción de propiedades. */
function canonicalJsonForAttempt(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalJsonForAttempt).join(',')}]`
  if (typeof value === 'object') {
    // TR4 (contención final): igual que JSON.stringify — los miembros con valor undefined se
    // OMITEN. Sin esto, {campo: undefined} y el mismo payload sin el campo (que Axios envía
    // BYTE-IDÉNTICOS) producían claves distintas y un retry shape-normalizado podía duplicar
    // una operación ya completada por el servidor.
    const keys = Object.keys(value as Record<string, unknown>)
      .filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJsonForAttempt((value as Record<string, unknown>)[k])}`).join(',')}}`
  }
  return JSON.stringify(value)
}
export function stableAttemptHash(value: unknown): string {
  const s = canonicalJsonForAttempt(value ?? null)
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0
  return h.toString(36)
}

/** Clave del intento: la primera llamada la crea; los reintentos la reutilizan. */
export function idempotencyKeyForAttempt(scope: string): string {
  let key = idempotencyAttemptKeys.get(scope)
  if (!key) {
    key = `${scope}:${randomKeySuffix()}`
    idempotencyAttemptKeys.set(scope, key)
  }
  return key
}

/** ¿El error admite reintentar con LA MISMA clave (replay M-6)? */
export function isRetryableIdempotencyError(error: any): boolean {
  const status = error?.response?.status ?? error?.status
  if (status === undefined || status === null) return true // red/timeout: el replay es seguro
  if (Number(status) >= 500) return true
  const data = error?.response?.data ?? error?.data ?? {}
  if (Number(status) === 409 && (data.retryable === true || data.code === 'IDEMPOTENCY_OPERATION_IN_PROGRESS')) return true
  return false
}

/** Liquida el intento tras una respuesta terminal (la próxima operación genera clave nueva). */
export function settleIdempotencyAttempt(scope: string): void {
  idempotencyAttemptKeys.delete(scope)
}

/** Sólo para specs (src/tests/v2a-compat): vaciar el registro de intentos. */
export function __resetIdempotencyAttempts(): void {
  idempotencyAttemptKeys.clear()
}

class InventoryService {
  private baseUrl = '/inventory'
  private api = apiClient

  // Limpia filtros eliminando null/undefined/cadenas vacías y arrays vacíos
  private sanitizeFilters(raw: Record<string, any> = {}): Record<string, any> {
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(raw || {})) {
      if (v == null) continue
      if (typeof v === 'string' && v.trim() === '') continue
      if (Array.isArray(v) && v.length === 0) continue
      out[k] = v
    }
    return out
  }

  // P13-V2B (Property 8): toda petición de lista sale ACOTADA — default + máximo alineados con
  // el cap de servidor V2A-GAP-01 (200). Ninguna vista puede pedir "el mundo entero".
  private boundedListParams(params: { page?: number | string; limit?: number | string } = {}, defaultLimit = 200): { page: number; limit: number } {
    const limit = Math.min(Math.max(parseInt(String(params.limit ?? defaultLimit)) || defaultLimit, 1), 200)
    const page = Math.max(parseInt(String(params.page ?? 1)) || 1, 1)
    return { page, limit }
  }

  // ==========================================
  // 📦 GESTIÓN DE INVENTARIO CENTRAL
  // ==========================================

  /**
   * Obtiene el inventario central con filtros
   */
  async getCentralInventory(
    filters: InventoryFilters = {},
    paging: { page?: number; limit?: number } = {}
  ): Promise<InventoryResponse<InventoryItem[]>> {
    // P13-V2B (Property 3 + 8): un error del servidor SE PROPAGA mapeado — jamás se degrada a
    // lista vacía "exitosa" — y la petición sale acotada contra el cap V2A-GAP-01 (≤200) con la
    // paginación del servidor expuesta al consumidor.
    try {
      const params = this.sanitizeFilters(filters as any)
      const { page, limit } = this.boundedListParams(paging)
      // skipUnwrap: el interceptor del api-client extrae `data` del sobre {success,data} y
      // DESTRUYE los hermanos (pagination/breakdown); este lector necesita el sobre completo.
      const response = await measureApi(
        () => this.api.get(`${this.baseUrl}/central`, { params: { ...params, page, limit }, skipUnwrap: true } as any),
        { label: 'inventory.getCentral', meta: { filters: Object.keys(params).length } }
      )

      const payload: any = response.data
      const rawInventoryData = payload?.data
      const pagination = payload?.pagination
      // Un sobre no reconocido NO es una lista vacía: es un contrato roto y debe verse.
      if (!Array.isArray(rawInventoryData)) {
        const err: any = new Error('Respuesta de inventario central con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }

      const inventoryData: InventoryItem[] = rawInventoryData.map((item: any) => normalizeCentralInventoryItem(item))

      // ⚖️ Dedupe defensivo por code DENTRO de la página (preserva el más reciente)
      const dedupMap = new Map<string, { item: InventoryItem; ts: number }>()
      for (const it of inventoryData) {
        const key = it.code
        const ts = new Date(it.lastUpdated || it.createdAt).getTime()
        const prev = dedupMap.get(key)
        if (!prev || ts > prev.ts) {
          dedupMap.set(key, { item: it as InventoryItem, ts })
        }
      }
      const deduped = Array.from(dedupMap.values()).map(v => v.item)
      deduped.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())

      return {
        data: deduped,
        total: pagination?.total ?? deduped.length,
        page: pagination?.page ?? page,
        limit: pagination?.limit ?? limit,
        filters
      }
    } catch (error) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Obtiene inventario por administración
   */
  async getAdministrationInventory(
    administrationId: string | number,
    filters: InventoryFilters = {},
    paging: { page?: number; limit?: number } = {}
  ): Promise<InventoryResponse<InventoryItem[]>> {
    // P13-V2B: el id de administración es CONTEXTO DE VISTA dirigido por la ruta — el servidor
    // sólo lo acepta como coincidir-o-estrechar (partición ajena ⇒ cero filas / concealed-404).
    // El sobre real del backend es { success, data: { items, pagination } }
    // (AdministrationInventoryController.getInventoryByAdmin): el parseo anterior lo descartaba
    // y la vista por administración renderizaba VACÍO en silencio.
    try {
      const params = this.sanitizeFilters(filters as any)
      const { page, limit } = this.boundedListParams(paging)
      const url = `${this.baseUrl}/administrations/${administrationId}/inventory`
      const response = await this.api.get(url, { params: { ...params, page, limit } })
      const payload: any = response.data?.data ?? response.data
      const raw = Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : null)
      if (!raw) {
        const err: any = new Error('Respuesta de inventario por administración con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }
      const items: InventoryItem[] = raw.map((it: any) => normalizeCentralInventoryItem(it))
      const pagination = payload?.pagination
      return {
        data: items,
        total: pagination?.total_items ?? items.length,
        page: pagination?.current_page ?? page,
        limit: pagination?.per_page ?? limit,
        filters: params
      }
    } catch (error) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Obtiene un artículo específico del inventario central por ID
   */
  async getCentralInventoryItem(itemId: string | number): Promise<InventoryItem> {
    try {
      // Enterprise: usar /products/:id (ruta legacy /central/:id eliminada)
      const response = await this.api.get(`${this.baseUrl}/products/${itemId}`)
      // Enterprise controller devuelve { success, data: { ...producto, qc?, attachments? } }
      const raw = response.data?.data || response.data
      const item = raw?.product || raw?.items || raw
      // Normalizar al mismo shape que getCentralInventory
      return normalizeCentralInventoryItem(item)
    } catch (error) {
      console.error('Error obteniendo item de inventario central:', (error as any)?.response?.data || error)
      throw error
    }
  }

  /**
   * Actualiza un artículo de inventario
   */
  async updateItem(itemId: string, itemData: Partial<InventoryItem>): Promise<InventoryItem> {
    try {
      // Mapear posibles campos legacy recibidos desde capas superiores (defensivo)
      const mapped: any = { ...itemData }
      if ((mapped as any).item_code && !mapped.code) mapped.code = (mapped as any).item_code
      if ((mapped as any).unit_cost != null && mapped.unitPrice == null) mapped.unitPrice = (mapped as any).unit_cost
      if ((mapped as any).minimum_stock != null && mapped.minStock == null) mapped.minStock = (mapped as any).minimum_stock
      if ((mapped as any).maximum_stock != null && mapped.maxStock == null) mapped.maxStock = (mapped as any).maximum_stock
      if ((mapped as any).current_stock != null && mapped.currentStock == null) mapped.currentStock = (mapped as any).current_stock
      // Enviar sólo campos enterprise válidos
      const allowed = ['code','name','description','category','status','unitPrice','minStock','maxStock','currentStock','supplier','location','unit','image']
      const payload: any = {}
      for (const k of allowed) if (mapped[k] !== undefined) payload[k] = mapped[k]
      const response = await this.api.put(`${this.baseUrl}/products/${itemId}`, payload)
      const data = response.data?.data || response.data
      const product = data?.product || data
      return product as InventoryItem
    } catch (error) {
      console.error('Error actualizando artículo:', error)
      throw error
    }
  }

  /**
   * Crea un nuevo artículo de inventario
   */
  async createItem(itemData: Omit<InventoryItem, 'id' | 'createdAt' | 'lastUpdated'>): Promise<InventoryItem> {
    try {
      // Mapear posibles campos legacy a enterprise
      const mapped: any = { ...itemData }
      if ((mapped as any).item_code && !mapped.code) mapped.code = (mapped as any).item_code
      if ((mapped as any).unit_cost != null && mapped.unitPrice == null) mapped.unitPrice = (mapped as any).unit_cost
      if ((mapped as any).minimum_stock != null && mapped.minStock == null) mapped.minStock = (mapped as any).minimum_stock
      if ((mapped as any).maximum_stock != null && mapped.maxStock == null) mapped.maxStock = (mapped as any).maximum_stock
      if ((mapped as any).current_stock != null && mapped.currentStock == null) mapped.currentStock = (mapped as any).current_stock
      // Filtrar sólo campos válidos enterprise
      const allowed = ['code','name','description','category','status','unitPrice','minStock','maxStock','currentStock','supplier','location','unit','image']
      const payload: any = {}
      for (const k of allowed) if (mapped[k] !== undefined) payload[k] = mapped[k]
      const response = await this.api.post(`${this.baseUrl}/products`, payload)
      return (response.data?.data || response.data) as InventoryItem
    } catch (error) {
      console.error('Error creando artículo:', error)
      throw error
    }
  }

  /**
   * Registra una recepción (GRN) en el inventario central para un producto específico.
   * Usa el endpoint enterprise: PUT /api/inventory/products/:id/stock
   * - movementType: 'RECEIVE' (entrada por recepción)
   * - reason: texto operativo (incluye referencia a OC/factura si se provee)
   * - referenceType/referenceId: se utilizan para trazabilidad (GRN/OC o FACTURA)
   */
  async receiveCentralStock(
    productId: string | number,
    payload: {
      quantity: number
      poNumber?: string
      invoiceNumber?: string
      deliveryNote?: string
      supplierName?: string
      notes?: string
      // URLs de evidencias ya subidas (opcional)
      attachments?: string[]
      // Metadatos adicionales (por si el backend los registra)
      metadata?: Record<string, any>
    }
  ): Promise<{ success?: boolean } | any> {
    if (!productId) throw new Error('productId es requerido para la recepción')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
  const { quantity, poNumber, invoiceNumber, deliveryNote, supplierName, notes, attachments, metadata } = payload
      // Construir motivo y referencias para auditoría
      const reasonParts = [
        'Recepción central (GRN)'
      ] as string[]
      if (supplierName) reasonParts.push(`Proveedor: ${supplierName}`)
      if (poNumber) reasonParts.push(`OC: ${poNumber}`)
      if (invoiceNumber) reasonParts.push(`Factura: ${invoiceNumber}`)
      if (deliveryNote) reasonParts.push(`Guía: ${deliveryNote}`)
      if (notes) reasonParts.push(`Notas: ${notes}`)
      const reason = reasonParts.join(' | ')

      // reference: priorizar factura; si no, OC; si no, GRN genérico
      let referenceType = 'GRN'
      let referenceId: string | undefined = undefined
      if (invoiceNumber) { referenceType = 'INVOICE'; referenceId = invoiceNumber }
      else if (poNumber) { referenceType = 'PO'; referenceId = poNumber }

      const body: any = {
        quantity: Math.abs(quantity),
        // P13-V2B (Property 2 — hallazgo V2B-F5): la etiqueta canónica del enum PG es 'ENTRY';
        // el literal legacy 'RECEIVE' no existe en `enum_stock_movements_movementType` y el GRN
        // "normal" respondía 500 en PostgreSQL (sólo "funcionaba" en SQLite, que no aplica
        // enums). El contexto GRN/OC/factura viaja en reason/referenceType/referenceId.
        movementType: 'ENTRY',
        reason,
        referenceType,
        referenceId,
        attachments: Array.isArray(attachments) && attachments.length ? attachments : undefined,
        metadata: metadata && Object.keys(metadata).length ? metadata : undefined
      }

      const response = await this.api.put(`${this.baseUrl}/products/${productId}/stock`, body)
      return (response as any).data?.data || (response as any).data || response
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  // ==========================================
  // 🧪 CONTROL DE CALIDAD (QC)
  // ==========================================

  /**
   * Envía unidades a revisión (QC). Incrementa reviewStock.
   * Backend: POST /api/inventory/products/:id/qc/receive
   */
  async receiveToQC(
    productId: string | number,
    payload: { quantity: number; notes?: string; attachments?: string[]; metadata?: Record<string, any> }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        notes: payload.notes,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined,
        metadata: payload.metadata && Object.keys(payload.metadata).length ? payload.metadata : undefined
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/qc/receive`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Acepta unidades desde revisión hacia disponible (currentStock).
   * Backend: POST /api/inventory/products/:id/qc/accept
   */
  async qcAccept(
    productId: string | number,
    payload: { quantity: number; notes?: string; attachments?: string[] }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        notes: payload.notes,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/qc/accept`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Rechaza unidades desde revisión (reduce reviewStock).
   * Backend: POST /api/inventory/products/:id/qc/reject
   */
  async qcReject(
    productId: string | number,
    payload: { quantity: number; notes?: string; attachments?: string[] }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        notes: payload.notes,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/qc/reject`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Envía unidades a cuarentena (desde revisión QC o desde stock disponible por defecto post-aceptación).
   * Backend: POST /api/inventory/products/:id/qc/quarantine
   * 
   * @param source - 'review' (desde reviewStock) | 'current' (desde currentStock, defecto post-aceptación)
   */
  async qcQuarantine(
    productId: string | number,
    payload: { 
      quantity: number
      source?: 'review' | 'current'  // ✨ NUEVO: Indica origen del stock
      reason?: string                // ✨ NUEVO: Motivo detallado
      notes?: string
      attachments?: string[]
      metadata?: Record<string, any> // ✨ NUEVO: Metadatos adicionales
    }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        source: payload.source,        // ✨ Pasar parámetro source al backend
        reason: payload.reason,         // ✨ Pasar reason (prioridad sobre notes)
        notes: payload.notes,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined,
        metadata: payload.metadata      // ✨ Pasar metadata
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/qc/quarantine`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Libera unidades de cuarentena (las vuelve disponibles para venta)
   * Backend: POST /api/inventory/products/:id/quarantine/release
   */
  async quarantineRelease(
    productId: string | number,
    payload: {
      quantity: number
      reason?: string
      attachments?: string[]
    }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        reason: payload.reason,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/quarantine/release`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error: any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Da de baja unidades en cuarentena (destrucción física)
   * Backend: POST /api/inventory/products/:id/quarantine/dispose
   */
  async quarantineDispose(
    productId: string | number,
    payload: {
      quantity: number
      disposalType: 'destruction' | 'expiration' | 'damaged' | 'other'
      reason?: string
      attachments?: string[]
    }
  ): Promise<any> {
    if (!productId) throw new Error('productId es requerido')
    if (!payload || typeof payload.quantity !== 'number' || payload.quantity <= 0) {
      throw new Error('quantity debe ser un número positivo')
    }
    if (!payload.disposalType) {
      throw new Error('disposalType es requerido')
    }
    try {
      const body: any = {
        quantity: Math.abs(payload.quantity),
        disposalType: payload.disposalType,
        reason: payload.reason,
        attachments: Array.isArray(payload.attachments) && payload.attachments.length ? payload.attachments : undefined
      }
      const res = await this.api.post(`${this.baseUrl}/products/${productId}/quarantine/dispose`, body)
      return (res as any).data?.data || (res as any).data || res
    } catch (error: any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Obtiene balances de QC del producto.
   * Backend: GET /api/inventory/products/:id/qc
   */
  async getQcBalances(productId: string | number): Promise<{ reviewStock: number; quarantinedStock: number }> {
    if (!productId) throw new Error('productId es requerido')
    try {
      const res = await this.api.get(`${this.baseUrl}/products/${productId}/qc`)
      const data = (res as any).data?.data || (res as any).data || {}
      return {
        reviewStock: Number(data.reviewStock || 0),
        quarantinedStock: Number(data.quarantinedStock || 0)
      }
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  // P13-V2B (ATTACHMENT_CONTRACT, mitad FE): `attachEvidence` (éxito falso — tragaba todo fallo
  // y retornaba normal contra una superficie de subida 410) y `deleteAttachment` se ELIMINAN con
  // cero consumidores. El retiro del escritor backend es de V2A
  // (INVENTORY_ATTACHMENT_EFFECTIVE_WRITERS = 0, SG-V2-05/05b); la canonicalización pertenece a
  // V5B. Sólo sobrevive la proyección de LECTURA por producto (abajo).

  /**
   * Elimina un artículo de inventario
   */
  async deleteItem(itemId: string): Promise<void> {
    try {
      await this.api.delete(`${this.baseUrl}/products/${itemId}`)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 409) {
        // Evitar ruido: 409 es esperado en este dominio, usar descontinuar
        console.debug('� deleteItem recibió 409 (conflicto por dependencias). Sugerencia: usar discontinueItem()', error?.response?.data)
      } else {
        console.error('Error eliminando artículo:', error)
      }
      throw error
    }
  }

  /**
   * Descontinúa un artículo de inventario (soft delete)
   */
  async discontinueItem(itemId: string): Promise<InventoryItem> {
    try {
      // Enterprise: no existe endpoint dedicado; usar update status=DISCONTINUED
      const response = await this.api.put(`${this.baseUrl}/products/${itemId}`, { status: 'DISCONTINUED' })
      const data = response.data?.data || response.data
      return (data?.product || data) as InventoryItem
    } catch (error:any) {
      console.error('Error descontinuando artículo:', error)
      throw error
    }
  }

  /**
   * Reactiva un artículo descontinuado (vuelve a ACTIVE). Permite opcionalmente actualizar campos.
   */
  async reactivateItem(itemId: string, patch: Partial<InventoryItem> = {}): Promise<InventoryItem> {
    try {
      const payload = { status: 'ACTIVE', ...patch }
      const response = await this.api.put(`${this.baseUrl}/products/${itemId}`, payload)
      const data = response.data?.data || response.data
      return (data?.product || data) as InventoryItem
    } catch (error:any) {
      console.error('Error reactivando artículo:', error)
      throw error
    }
  }

  // ==========================================
  // 🚨 GESTIÓN DE ALERTAS
  // ==========================================

  /**
   * Obtiene alertas de stock bajo
   */
  async getStockAlerts(
    params: { administration_id?: string | number; limit?: number; page?: number } = {}
  ): Promise<InventoryResponse<StockAlert[]>> {
    // P13-V2B (Property 3 + 8): errores propagados mapeados (nunca lista vacía "exitosa");
    // petición acotada (≤200) y `total` del COUNT real del servidor.
    try {
      const query: any = {}
      if (params?.administration_id != null && params.administration_id !== '') {
        query.administration_id = String(params.administration_id)
      }
      const paged = this.boundedListParams({ limit: params.limit, page: params.page }, 200)
      query.limit = paged.limit
      query.page = paged.page
      // skipUnwrap: el `count` (COUNT real del servidor) es hermano de `data` en el sobre.
      const response = await measureApi(() => this.api.get(`${this.baseUrl}/alerts`, { params: query, skipUnwrap: true } as any), { label: 'inventory.getStockAlerts' })

      const rawAlertsData = response.data?.data
      if (!Array.isArray(rawAlertsData)) {
        const err: any = new Error('Respuesta de alertas con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }

      // Normalizar datos del backend para coincidir con la interfaz del frontend
      const alertsData = rawAlertsData.map((alert: any) => ({
        id: alert.id,
        itemId: alert.inventoryId || alert.itemId,
        itemName: alert.inventoryItem?.name || alert.item?.name || alert.itemName || alert.message || 'Artículo desconocido',
        itemCode: alert.inventoryItem?.code || alert.item?.code || alert.itemCode || '',
        currentStock: alert.currentValue || alert.currentStock || 0,
        minStock: alert.thresholdValue || alert.minStock || 0,
        severity: alert.severity || 'warning',
        message: alert.message || '',
        createdAt: alert.createdAt || new Date().toISOString(),
        isRead: alert.isRead || false,
        agencyId: alert.agencyId || '',
        categoryId: alert.categoryId || ''
      }))

      return {
        data: alertsData,
        total: response.data?.count ?? alertsData.length
      }
    } catch (error: any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Resuelve una alerta de inventario.
   * Backend canónico: PUT /api/inventory/alerts/:id/resolve (rol de escritura; el alcance es del
   * servidor — sin cabeceras derivadas de localStorage). El POST anterior era un 404 silencioso.
   * "Descartar" una alerta ES resolverla: no existe (ni existió) un DELETE en el servidor.
   */
  async resolveAlert(alertId: string): Promise<{ success?: boolean } | any> {
    try {
      const response = await this.api.put(`${this.baseUrl}/alerts/${alertId}/resolve`)
      return (response as any).data?.data || (response as any).data || response
    } catch (error: any) {
      throw extractInventoryError(error)
    }
  }

  // ==========================================
  // 📋 SOLICITUDES DE REPOSICIÓN
  // ==========================================

  /**
   * Crea una solicitud de reposición de stock
   */
  async createStockRequest(requestData: Omit<StockRequest, 'id' | 'createdAt' | 'updatedAt'>): Promise<StockRequest> {
    // P13-V2B (Property 2): el relleno de compatibilidad ACF-9 (camelCase→snake_case) está
    // RETIRADO — el diálogo vivo habla la forma canónica en origen (InventoryRequestDialog).
    // Un payload que sólo trae la forma legacy ya no se traduce en silencio: falla con el 400
    // del backend, visible y honesto. Los campos de alcance siguen sin reenviarse:
    // administración/grupo/agencia los deriva el SERVIDOR (PD-V2-06).
    const normalizedRequest = withNormalized({ ...(requestData as any) }, 'inventory.createStockRequest.normalize') as typeof requestData

    // M-7 / PD-V2-08: cantidades ENTERAS en toda la cadena — rechazo local coherente con el 400
    // del backend (jamás se envía un decimal).
    const quantities: any[] = []
    if ((normalizedRequest as any)?.requested_quantity != null) quantities.push((normalizedRequest as any).requested_quantity)
    if ((normalizedRequest as any)?.quantity != null) quantities.push((normalizedRequest as any).quantity)
    for (const it of ((normalizedRequest as any)?.items || [])) if (it?.quantity != null) quantities.push(it.quantity)
    const fractional = quantities.find((q) => Number.isFinite(Number(q)) && !Number.isInteger(Number(q)))
    if (fractional !== undefined) {
      const err: any = new Error(`Las cantidades de inventario deben ser enteras (PD-V2-08); recibido: ${fractional}`)
      err.code = 'INVALID_QUANTITY'
      throw err
    }

    // C7: UNA clave por INTENTO (scope = payload estable, o el scope explícito del caller);
    // un reenvío del mismo intento reutiliza la clave (replay M-6), un intento nuevo la renueva.
    const attemptScope = (requestData as any).__idempotencyKey
      ? `request.create:${(requestData as any).__idempotencyKey}`
      : `request.create:${stableAttemptHash(normalizedRequest)}`
    const key = idempotencyKeyForAttempt(attemptScope)
    try {
      console.log('🔄 inventoryService.createStockRequest - Enviando solicitud:', normalizedRequest)
      const headers: any = { 'Idempotency-Key': key }
      // C7 (R0-INV-21): endpoint canónico PLURAL /inventory/requests (el singular era 404).
      const exec = () => this.api.post(`${this.baseUrl}/requests`, normalizedRequest, { headers })
      const response = await measureApi(exec, { label: 'inventory.createStockRequest', meta: { items: (normalizedRequest as any)?.items?.length || 0, idempotencyKey: key } })
      console.log('✅ inventoryService - Solicitud creada:', response.data)
      settleIdempotencyAttempt(attemptScope)
      return { ...(response.data as any), __idempotencyKey: key } as StockRequest & { __idempotencyKey?: string }
    } catch (error:any) {
      if (!isRetryableIdempotencyError(error)) settleIdempotencyAttempt(attemptScope)
      throw extractInventoryError(error)
    }
  }

  /**
   * Creación de solicitud desde el NAMESPACE de supervisor de campo
   * (POST /field-supervisor/inventory/request).
   *
   * CLOSURE REPAIR (FINDING A). Es una entrada comercial VIVA y distinta de
   * `createStockRequest`: el panel de campo publica en su propio namespace, no en
   * `/inventory/requests`. Tras V2A el backend exige `Idempotency-Key` fail-closed en las ocho
   * operaciones comerciales, incluida ésta, de modo que la ruta activa respondía
   * 400 IDEMPOTENCY_KEY_REQUIRED. La corrección NO introduce una segunda implementación de
   * idempotencia: consume EXACTAMENTE la misma autoridad de ciclo de vida que el resto del
   * frontend (una clave por intento comercial · el reintento de red/5xx/409-retryable conserva la
   * clave para que el backend haga REPLAY · la respuesta terminal la liquida · un intento
   * conscientemente nuevo genera una clave nueva, porque el scope deriva del payload).
   *
   * La respuesta se devuelve TAL CUAL la entrega la instancia axios canónica (misma instancia que
   * usaba `ApiClient.post`), de modo que el consumidor conserva su contrato de respuesta y su
   * extracción de errores sin cambio alguno.
   */
  async createFieldSupervisorStockRequest(requestData: any): Promise<any> {
    // M-7 / PD-V2-08: cantidades ENTERAS extremo a extremo — nunca se envía un decimal.
    const quantity = requestData?.quantity
    if (quantity != null && Number.isFinite(Number(quantity)) && !Number.isInteger(Number(quantity))) {
      const err: any = new Error(`Las cantidades de inventario deben ser enteras (PD-V2-08); recibido: ${quantity}`)
      err.code = 'INVALID_QUANTITY'
      throw err
    }

    const attemptScope = `field-supervisor.request.create:${stableAttemptHash(requestData)}`
    const key = idempotencyKeyForAttempt(attemptScope)
    try {
      const response = await this.api.post('/field-supervisor/inventory/request', requestData, {
        headers: { 'Idempotency-Key': key },
      })
      settleIdempotencyAttempt(attemptScope)
      return response
    } catch (error: any) {
      if (!isRetryableIdempotencyError(error)) settleIdempotencyAttempt(attemptScope)
      throw error
    }
  }

  /**
   * Lista solicitudes de inventario — ÚNICO lector de solicitudes del frontend.
   * Backend canónico: GET /api/inventory/requests → { success, data: { requests, pagination } }
   * (el lector legacy duplicado getStockRequests fue retirado con cero consumidores).
   *
   * P13-V2B: sobre ESTRICTO — una forma no reconocida lanza, jamás se degrada a "sin
   * solicitudes"; la petición sale acotada (≤200) y la paginación del servidor se expone.
   */
  async listRequests(params: Record<string, any> = {}): Promise<InventoryResponse<any[]>> {
    try {
      const { page, limit } = this.boundedListParams(params)
      const response = await this.api.get(`/inventory/requests`, { params: { ...params, page, limit } })
      const payload = (response as any)?.data
      // El interceptor puede haber desenvuelto {success,data}: acepta ambas formas del MISMO
      // contrato canónico ({data:{requests,pagination}} o {requests,pagination}); cualquier
      // otra forma sigue lanzando.
      const body = payload?.data ?? payload
      const requests = Array.isArray(body?.requests) ? body.requests : null
      if (!requests) {
        const err: any = new Error('Respuesta de solicitudes con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }
      const pagination = body?.pagination
      return {
        data: requests,
        total: pagination?.total_items ?? requests.length,
        page: pagination?.current_page ?? page,
        limit: pagination?.per_page ?? limit
      }
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Obtiene detalle de una solicitud por ID
   * Backend: GET /api/inventory/requests/:id
   */
  async getRequestById(requestId: string | number): Promise<any> {
    try {
      const response = await this.api.get(`/inventory/requests/${requestId}`)
      const payload: any = (response as any)?.data
      if (payload?.data) return payload.data
      return payload
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Aprueba una solicitud de inventario
   * Backend: PUT /api/inventory/requests/:id/approve
   */
  async approveRequest(requestId: string | number, approvedQuantity: number, notes = ''): Promise<any> {
    // C7 + TR4 (reparación terminal): el INTENTO es id + PAYLOAD MATERIAL — tras un fallo
    // reintentable, reenviar el MISMO payload reutiliza la clave (replay M-6), pero editar
    // cantidad/notas es un intento NUEVO con clave nueva (antes: misma clave con payload
    // cambiado ⇒ IDEMPOTENCY_PAYLOAD_CONFLICT del backend en vez de ejecutar la corrección).
    const payload: any = { approved_quantity: approvedQuantity, notes, contextMode: 'central' }
    const attemptScope = `request.approve:${requestId}:${stableAttemptHash(payload)}`
    const key = idempotencyKeyForAttempt(attemptScope)
    try {
      const response = await this.api.put(`/inventory/requests/${requestId}/approve`, payload, { headers: { 'Idempotency-Key': key } })
      settleIdempotencyAttempt(attemptScope)
      return (response as any).data
    } catch (error:any) {
      if (!isRetryableIdempotencyError(error)) settleIdempotencyAttempt(attemptScope)
      throw extractInventoryError(error)
    }
  }

  /**
   * Cancela una solicitud de inventario
   * Backend: PUT /api/inventory/requests/:id/cancel
   */
  async cancelRequest(requestId: string | number, reason: string): Promise<any> {
    // C7 + TR4: mismo cierre que approve — una razón editada tras un fallo reintentable es un
    // intento NUEVO; la misma razón reenviada reutiliza la clave (replay M-6).
    const payload: any = { cancellation_reason: reason }
    const attemptScope = `request.cancel:${requestId}:${stableAttemptHash(payload)}`
    const key = idempotencyKeyForAttempt(attemptScope)
    try {
      const response = await this.api.put(`/inventory/requests/${requestId}/cancel`, payload, { headers: { 'Idempotency-Key': key } })
      settleIdempotencyAttempt(attemptScope)
      return (response as any).data
    } catch (error:any) {
      if (!isRetryableIdempotencyError(error)) settleIdempotencyAttempt(attemptScope)
      throw extractInventoryError(error)
    }
  }

  // ==========================================
  // 🔄 TRANSFERENCIAS — RETIRADAS (P13-V2B, superseded-map fila 6)
  // ==========================================
  // `createTransfer` (PUT /inventory/transfer SINGULAR, generador de clave aleatoria POR LLAMADA
  // — la única mutación comercial fuera del ciclo de vida M-6 congelado) y `getTransfers`
  // (GET /inventory/transfers, ruta inexistente en el servidor) se RETIRAN con su sujeto:
  // cero consumidores vivos. El movimiento comercial entre inventarios viaja por las órdenes
  // de salida (outboundService) — una sola autoridad de traslado.

  // (Sección Outbound eliminada: usar outboundService.* para crear/listar/actualizar órdenes de salida)

  // ==========================================
  // �📊 ESTADÍSTICAS Y REPORTES
  // ==========================================

  /**
   * Obtiene estadísticas de inventario (Central)
   */
  async getInventoryStats(): Promise<InventoryResponse<InventoryStats>> {
    // P13-V2B (Property 3): los KPIs son verdad del SERVIDOR — un fallo se propaga mapeado en
    // vez de degradarse a estadísticas vacías "exitosas" que la vista rellenaría localmente.
    try {
      const response = await measureApi(() => this.api.get(`${this.baseUrl}/stats`), { label: 'inventory.getStats' })
      const statsData = response.data?.data || response.data
      if (!statsData || typeof statsData !== 'object') {
        const err: any = new Error('Respuesta de estadísticas con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }
      return { data: statsData }
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  // P13-V7B · RETIRED: `getAdministrationMetrics` (`GET /inventory/metrics/:administrationId`). The
  // metrics router registers `/outbound` alone, so the path fell through to the retired regional
  // inventory router and answered 410 LEGACY_SURFACE_RETIRED (403 for administration_manager). Its only
  // caller, the administration scope of InventoryManagement, no longer requests it.

  /**
   * Obtiene movimientos de inventario — petición SIEMPRE acotada al cap real del servidor
   * (V2A: default 50, máximo 200). El caller recibe también el total para poder señalar
   * truncamiento honestamente.
   */
  async getMovements(filters: any = {}): Promise<InventoryResponse<InventoryMovement[]>> {
    try {
      const { page, limit } = this.boundedListParams(filters, 50)
      // skipUnwrap: `pagination` es hermano de `data` en el sobre del lector de movimientos.
      const response = await this.api.get(`${this.baseUrl}/movements`, { params: { ...filters, page, limit }, skipUnwrap: true } as any)
      const payload: any = response.data
      const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : null)
      if (!rows) {
        const err: any = new Error('Respuesta de movimientos con forma no reconocida.')
        err.code = 'UNRECOGNIZED_RESPONSE_SHAPE'
        throw err
      }
      return {
        data: rows,
        total: payload?.pagination?.total ?? rows.length,
        page: payload?.pagination?.page ?? page,
        limit: payload?.pagination?.limit ?? limit
      }
    } catch (error) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Lista adjuntos por producto — proyección de LECTURA de evidencias legacy (solo lectura;
   * la superficie de subida está retirada). Un error se propaga mapeado: no se degrada a
   * "sin adjuntos". (`listAttachmentsByMovement` se eliminó con cero consumidores.)
   */
  async listAttachmentsByProduct(productId: string | number): Promise<InventoryResponse<any[]>> {
    try {
      const response = await this.api.get(`${this.baseUrl}/products/${productId}/attachments`)
      const payload: any = (response as any)?.data
      const data = payload?.data || payload || []
      return { data, total: Array.isArray(data) ? data.length : 0 }
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Genera un reporte de inventario
   */
  async generateReport(reportType: string, filters: any = {}): Promise<InventoryReport> {
    try {
      const response = await this.api.post(`${this.baseUrl}/reports`, {
        type: reportType,
        filters
      })
      return response.data
    } catch (error) {
      console.error('Error generando reporte:', error)
      throw error
    }
  }

  // ==========================================
  // 🏢 AGENCIAS
  // ==========================================

  /**
   * Obtiene lista de agencias
   */
  async getAgencies(): Promise<InventoryResponse<Agency[]>> {
    // P13-V2B (Property 3): un fallo al listar agencias se propaga mapeado — un selector de
    // destinos vacío por error ya no se disfraza de "no hay agencias".
    try {
      // P13-PROD-SCALE-BOUNDED-READS-WS1: /agencies answers one bounded page. The transfer-destination
      // picker needs the WHOLE catalogue: every page is read (envelope kept), or the error propagates.
      const agenciesData = await fetchAllPointPages(sharedClientPointPages(this.api, '/agencies'))
      return {
        data: agenciesData,
        total: agenciesData.length
      }
    } catch (error) {
      throw extractInventoryError(error)
    }
  }

}

export const inventoryService = new InventoryService()
